import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Badge } from '@core/components/ui/Badge';
import { Tooltip } from '@core/components/ui/Tooltip';
import { ResponsiveDataList } from '@core/components/ui/ResponsiveDataList';
import { formatDateCompact } from '@core/components/ui/utils/formatters';
import { useDismissAttentionMutation } from '../api/conversationsApi';
import { getSourceBadge } from '../utils/conversationUtils';

function NeedsAttentionIndicator({ label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-error">
      <span className="relative flex size-2 shrink-0">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-error opacity-75" />
        <span className="relative inline-flex size-2 rounded-full bg-error" />
      </span>
      {label}
    </span>
  );
}

NeedsAttentionIndicator.propTypes = {
  label: PropTypes.string.isRequired,
};

export function ConversationTable({ conversations, onRowClick, isAttentionTab = false }) {
  const { t } = useTranslation();
  const [dismissAttention] = useDismissAttentionMutation();

  // Define table columns (TanStack Table format)
  const columns = useMemo(() => [
    {
      id: 'user',
      accessorFn: (row) => `${row.user.first_name} ${row.user.last_name}`,
      header: () => t('conversations.user'),
      cell: ({ row }) => {
        const conversation = row.original;
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-default">
              {conversation.user.first_name} {conversation.user.last_name}
            </span>
            {conversation.needs_attention && (
              <NeedsAttentionIndicator label={t('conversations.needsAttentionLabel', 'Requires attention')} />
            )}
          </div>
        );
      },
      enableSorting: true,
      enableGlobalFilter: true,
    },
    {
      accessorKey: 'last_message_preview',
      header: () => t('conversations.message'),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="text-sm text-default max-w-xs truncate">
            {row.original.last_message_preview}
          </div>
          <Badge variant="secondary">{row.original.message_count}</Badge>
        </div>
      ),
      enableSorting: false,
      enableGlobalFilter: true,
    },
    {
      accessorKey: 'source',
      header: () => t('conversations.tags'),
      cell: ({ row }) => {
        const conversation = row.original;
        const sourceBadge = getSourceBadge(conversation.source, t);
        const isOnboarding = conversation.conversation_type === 'onboarding';
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant={sourceBadge.variant}>
              {sourceBadge.label}
            </Badge>
            {isOnboarding && (
              <Badge variant="warning">
                {t('conversations.typeOnboarding', 'Onboarding')}
              </Badge>
            )}
            {!isAttentionTab && conversation.needs_attention && (
              <Tooltip content={conversation.attention_reason || t('conversations.urgentAttention')} maxWidth="xs">
                <span className="hs-tooltip-toggle inline-flex items-center gap-1">
                  <Badge variant="danger">
                    {t('conversations.urgentAttention')}
                  </Badge>
                  <button
                    type="button"
                    className="text-muted hover:text-default cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissAttention(conversation.id);
                    }}
                    title={t('conversations.dismissUrgent')}
                  >
                    <Icon icon="lucide:x" className="size-3" />
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
    },
    {
      id: 'window_24h',
      header: () => t('conversations.windowColumn'),
      cell: ({ row }) => {
        const conversation = row.original;
        return conversation.is_within_24h_window
          ? <Badge variant="success">{t('conversations.windowActive')}</Badge>
          : <Badge variant="status-inactive">{t('conversations.windowExpiredLabel')}</Badge>;
      },
      enableSorting: false,
      enableGlobalFilter: false,
    },
    {
      accessorKey: 'last_active',
      header: () => t('conversations.lastActive'),
      cell: (info) => (
        <span className="text-sm text-muted">
          {formatDateCompact(info.getValue())}
        </span>
      ),
      enableSorting: true,
      enableGlobalFilter: false,
    },
  ], [t, isAttentionTab]);

  // Get row props for styling based on conversation state
  const getRowProps = useCallback(() => {
    return {};
  }, []);

  // Mobile card renderer
  const renderMobileCard = useCallback((row) => {
    const conversation = row.original;
    const sourceBadge = getSourceBadge(conversation.source, t);

    return (
      <div
        className="p-4 border border-border-default rounded-xl hover:shadow-md transition-shadow cursor-pointer bg-surface"
        onClick={() => onRowClick(conversation.id)}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-medium text-default">
              {conversation.user.first_name} {conversation.user.last_name}
            </span>
            {conversation.needs_attention && (
              <NeedsAttentionIndicator label={t('conversations.needsAttentionLabel', 'Requires attention')} />
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant={sourceBadge.variant}>
              {sourceBadge.label}
            </Badge>
            {conversation.conversation_type === 'onboarding' && (
              <Badge variant="warning">
                {t('conversations.typeOnboarding', 'Onboarding')}
              </Badge>
            )}
          </div>
        </div>
        <div className="text-sm text-default truncate mb-2">
          {conversation.last_message_preview}
        </div>
        <div className="flex justify-between items-center text-xs text-muted">
          <span>{conversation.message_count} {t('conversations.messageCount')}</span>
          <span>{formatDateCompact(conversation.last_active)}</span>
        </div>
      </div>
    );
  }, [onRowClick, t]);

  return (
    <ResponsiveDataList
      data={conversations}
      columns={columns}
      renderMobileItem={renderMobileCard}
      emptyMessage={t('conversations.noConversations')}
      breakpoint="md"
      enableSorting={true}
      enableFiltering={false}
      getRowProps={getRowProps}
      onRowClick={(conversation) => onRowClick(conversation.id)}
      initialState={{ sorting: [{ id: 'last_active', desc: true }] }}
    />
  );
}

ConversationTable.propTypes = {
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      session_id: PropTypes.string.isRequired,
      user: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string.isRequired,
      }).isRequired,
      last_message_preview: PropTypes.string,
      message_count: PropTypes.number.isRequired,
      source: PropTypes.string.isRequired,
      created_at: PropTypes.string,
      last_active: PropTypes.string.isRequired,
      is_within_24h_window: PropTypes.bool,
      needs_attention: PropTypes.bool,
      attention_reason: PropTypes.string,
    })
  ).isRequired,
  onRowClick: PropTypes.func.isRequired,
  isAttentionTab: PropTypes.bool,
};
